export const PLUGIN_IMAGE_SLOTS = [
    { key: 'idIntro', label: 'ID instructions (start screen)' },
    {
        key: 'selfieIntro',
        label: 'Selfie instructions (ID captured and selfie-only start screen)'
    },
    { key: 'reviewRequired', label: 'Under review (done screen)' },
    { key: 'verified', label: 'Verified (done screen)' }
];
export const getPluginImageSlotState = (key, saved, pending) => {
    const pick = pending[key];
    if (typeof pick === 'string') {
        return { status: 'pending', previewSrc: pick };
    }
    if (pick === null) {
        return { status: 'removed' };
    }
    return saved[key]
        ? { status: 'saved', previewSrc: saved[key] }
        : { status: 'empty' };
};
export const withPluginImagePick = (pending, key, dataUri) => ({ ...pending, [key]: dataUri });
// Discards a pending pick; otherwise marks a saved image for deletion.
export const withPluginImageRemoved = (pending, saved, key) => {
    if (typeof pending[key] === 'string') {
        return withoutPluginImageChange(pending, key);
    }
    return saved[key] ? { ...pending, [key]: null } : pending;
};
export const withoutPluginImageChange = (pending, key) => {
    return Object.fromEntries(Object.entries(pending).filter(([k]) => k !== key));
};
// Returns only changed keys, or undefined when nothing changed.
export const buildPluginImagesPayload = (pending) => Object.keys(pending).length > 0 ? { ...pending } : undefined;
/** Largest accepted plugin image (1.5 MB, decimal); onboard-service enforces the same limit. */
export const MAX_PLUGIN_IMAGE_BYTES = 1_500_000;
const GENERIC_IMAGE_ERROR = 'Please try again.';
/** Readable reason for a failed image save; API errors carry per-field messages in `subErrors`. */
export const pluginImagesErrorMessage = (error) => {
    const subErrors = error?.subErrors;
    if (Array.isArray(subErrors)) {
        const messages = subErrors
            .map((sub) => sub?.message)
            .filter((message) => typeof message === 'string' && message.trim().length > 0);
        return messages.length > 0 ? messages.join('; ') : GENERIC_IMAGE_ERROR;
    }
    return error instanceof Error && error.message.trim().length > 0
        ? error.message
        : GENERIC_IMAGE_ERROR;
};
/** Drops the keys a save just stored, keeping any re-picked since it was sent. */
export const withoutSavedPluginImages = (current, sent) => Object.fromEntries(Object.entries(current).filter(([key, value]) => !(key in sent) || sent[key] !== value));
/**
 * Update makes two requests: the properties save, then the plugin image upload
 * (its own endpoint, since the files go to the bucket) when any images are
 * pending. Images are only sent once the properties save succeeds.
 */
export const saveWithPluginImages = async ({ saveProperties, saveImages, pending }) => {
    await saveProperties();
    const update = buildPluginImagesPayload(pending);
    if (!update) {
        return {};
    }
    try {
        return { storedImages: await saveImages(update) };
    }
    catch (error) {
        return {
            imagesError: error instanceof Error ? error : new Error(String(error))
        };
    }
};
export const readPngAsDataUri = async (file) => {
    if (file.type !== 'image/png') {
        throw new Error('Plugin images must be PNG files');
    }
    if (file.size > MAX_PLUGIN_IMAGE_BYTES) {
        throw new Error('Plugin images must be 1.5 MB or smaller');
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('error', () => {
            reject(new Error('Failed to read file'));
        });
        reader.addEventListener('load', () => {
            resolve(reader.result);
        });
        reader.readAsDataURL(file);
    });
};
